import { Box, Button, Tooltip, Typography } from '@mui/material'
import { useSystems } from 'api/systems/systems.api'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import styles from './ImpersonatingBanner.module.less'

interface Props {
  inline?: boolean
}

export default function ImpersonatingBanner({ inline }: Props): ReactElement | null {
  const { impersonatingUser, user, setUser, setImpersonatingUser } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { data: systems } = useSystems()
  if (!impersonatingUser) return null

  const systemRoles = (user?.system_roles || []) as any[]
  interface SystemPermission {
    description?: string
    permission?: string
    system?: string | number
    system_id?: number
    system_name?: string
  }
  const systemPermissions = (user?.system_permissions || []) as SystemPermission[]

  const roleNames = Array.from(new Set(systemRoles.map((r) => r.role || r.role_name || '').filter(Boolean)))
  // Group permissions by system display name
  const systemPermissionGroups = systemPermissions.reduce((map: Map<string, Set<string>>, r) => {
    const permission = (r.description || r.permission || '').trim()
    const sysId = r.system || r.system_id
    let sysLabel = r.system_name || ''
    if (systems && sysId) {
      const matched = systems.find((s) => s.id === sysId)
      if (matched) sysLabel = matched.display_name || matched.name || sysLabel
    }
    if (!sysLabel && r.system_name) sysLabel = r.system_name
    if (!sysLabel) sysLabel = String(sysId) || ''
    if (sysLabel && permission) {
      let set = map.get(sysLabel)
      if (!set) {
        set = new Set<string>()
        map.set(sysLabel, set)
      }
      set.add(permission)
    }
    return map
  }, new Map())
  const systemPermissionsEntries: [string, Set<string>][] = [...systemPermissionGroups.entries()]
    .map((e) => e as [string, Set<string>])
    .sort((a, b) => {
      return a[0].localeCompare(b[0])
    })

  const tooltipContentEl = (
    <div className={styles.ImpersonationBanner_TooltipContent}>
      {roleNames.length > 0 && (
        <div>
          <strong>Roles:</strong> {roleNames.join(', ')}
        </div>
      )}
      {systemPermissionsEntries.length > 0 && (
        <div style={{ marginTop: roleNames.length ? 6 : 0 }}>
          <strong>System permissions:</strong>
          <ul className={styles.ImpersonationBanner_PermissionList}>
            {systemPermissionsEntries.map(([systemName, perms]) => {
              const sortedPerms = Array.from(perms).sort((a, b) => a.localeCompare(b))
              return (
                <li key={systemName} className={styles.ImpersonationBanner_SystemItem}>
                  <span className={styles.ImpersonationBanner_SystemName}>{systemName}</span>
                  <ul className={styles.ImpersonationBanner_PermissionSubList}>
                    {sortedPerms.map((perm) => (
                      <li key={perm}>{perm}</li>
                    ))}
                  </ul>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )

  return (
    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <div className={inline ? styles.ImpersonationBannerInline : styles.ImpersonationBanner}>
        <Tooltip title={tooltipContentEl} placement="bottom">
          <Typography variant="body1" className={styles.ImpersonationBanner_Badge}>
            {t('Impersonating')} {user?.first_name} {user?.last_name} ({user?.email})
            <Button
              size="small"
              variant="contained"
              color="secondary"
              className={styles.ImpersonationBanner_StopButton}
              onClick={() => {
                setUser(impersonatingUser)
                setImpersonatingUser(null)
                navigate('/startpage', { replace: true })
                window.location.reload()
              }}
            >
              {t('Stop')}
            </Button>
          </Typography>
        </Tooltip>
      </div>
    </Box>
  )
}
